import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/archive';

function Brand() {
  return (
    <Link to="/" className="group flex items-center gap-2.5 shrink-0" aria-label="每日吃瓜网 首页">
      <span className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-background-50 shadow-sm">
        <i className="ri-chat-3-fill text-xl" aria-hidden="true" />
        <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-accent-400 ring-2 ring-background-50" />
      </span>
      <span className="leading-none">
        <span className="font-heading text-2xl tracking-wide text-primary-600">每日吃瓜</span>
        <span className="mt-1 block text-[10px] font-medium tracking-[0.3em] text-foreground-400">CHI·GUA·DAILY</span>
      </span>
    </Link>
  );
}

const navItems = () => [
  { to: '/', name: '首页' },
  ...categories.map((c) => ({ to: `/column/${c.slug}`, name: c.name })),
  { to: '/about', name: '关于我们' },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const items = navItems();

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = q.trim();
    if (kw) {
      navigate(`/search/${encodeURIComponent(kw)}`);
      setQ('');
      setSearchOpen(false);
      setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/70 bg-background-50/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Brand />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `inline-flex items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'bg-foreground-900 text-background-50' : 'text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950'
                }`
              }
            >
              {it.name}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            {searchOpen ? (
              <form onSubmit={onSearch} className="flex items-center gap-1 rounded-full bg-background-100 px-2 py-1">
                <i className="ri-search-line px-1 text-foreground-400" aria-hidden="true" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="搜索瓜料 / 明星 / 事件…"
                  className="w-44 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                <button type="submit" className="whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700" aria-label="提交搜索">
                  搜索
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-200/70 hover:text-foreground-950"
                aria-label="打开搜索"
              >
                <i className="ri-search-line text-lg" aria-hidden="true" />
              </button>
            )}
          </div>
          <button
            type="button"
            className="hidden whitespace-nowrap items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
            onClick={() => navigate('/about#contact')}
          >
            <i className="ri-mail-send-line text-base" aria-hidden="true" />
            投稿爆料
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-800 hover:bg-background-200/70 lg:hidden"
            aria-label={open ? '关闭菜单' : '打开菜单'}
            aria-expanded={open}
          >
            <i className={open ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} aria-hidden="true" />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 pb-5 pt-3 lg:hidden md:px-6">
          <form onSubmit={onSearch} className="mb-3 flex items-center gap-2 rounded-full bg-background-100 px-3 py-2">
            <i className="ri-search-line text-foreground-400" aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="搜索瓜料 / 明星 / 事件…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="whitespace-nowrap text-sm font-medium text-primary-600" aria-label="提交搜索">
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-3 text-sm font-medium ${
                    isActive ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-800'
                  }`
                }
              >
                {it.name}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative overflow-hidden bg-primary-900 text-primary-100">
      <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full bg-primary-700/30 blur-2xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-16 bottom-0 h-60 w-60 rounded-full bg-accent-600/20 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-background-50/10 text-background-50">
                <i className="ri-chat-3-fill text-xl" aria-hidden="true" />
              </span>
              <span className="font-heading text-2xl text-background-50">每日吃瓜</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-primary-200">
              专注娱乐圈八卦速递，明星塌房、爱豆恋情实锤、剧组爆料、综艺幕后与红毯名场面，一手瓜料持续更新。
            </p>
            <a
              href="mailto:hello@meirichigua.com"
              className="mt-4 inline-block text-sm text-primary-300 underline decoration-primary-400 underline-offset-4 transition-colors hover:text-background-50"
            >
              hello@meirichigua.com
            </a>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-background-50">热门栏目</h3>
            <ul className="mt-4 space-y-3 text-sm">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link to={`/column/${c.slug}`} className="inline-flex items-center gap-2 text-primary-200 transition-colors hover:text-background-50">
                    <i className={c.icon} aria-hidden="true" /> {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-background-50">快速导航</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/" className="text-primary-200 transition-colors hover:text-background-50">首页热瓜</Link></li>
              <li><Link to="/about" className="text-primary-200 transition-colors hover:text-background-50">关于我们</Link></li>
              <li><Link to="/about#faq" className="text-primary-200 transition-colors hover:text-background-50">吃瓜常见问题</Link></li>
              <li><a href="mailto:hello@meirichigua.com" className="text-primary-200 transition-colors hover:text-background-50">投稿与商务</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-background-50">栏目速览</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/column/${c.slug}`}
                  className="rounded-full bg-background-50/10 px-3 py-1.5 text-xs text-primary-100 transition-colors hover:bg-background-50 hover:text-primary-700"
                >
                  #{c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-background-50/15 pt-6 text-sm text-primary-300 sm:flex-row sm:items-center">
          <p>© {year} 每日吃瓜网 ChiGua Daily · 娱乐内容仅供参考，请理性吃瓜。</p>
          <p>联系邮箱：hello@meirichigua.com</p>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onScroll = () => setShowTop(window.scrollY > 480);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main id="main">
        <Outlet />
      </main>
      <Footer />
      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg transition-transform hover:scale-105"
          aria-label="回到顶部"
        >
          <i className="ri-arrow-up-line text-lg" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}